import { useMemo, useState } from 'react'
import { MagnifyingGlass, Package, Tag as TagIcon } from '@phosphor-icons/react'
import { istDateKey } from '@/lib/ist'
import { Input, Select } from '@/ui'
import { errorMessage } from '../../api/core'
import { gramsToKgString } from '../../api/mappers'
import {
  lookupPackable,
  pickPackLine,
  useCancelPackRun,
  useCreatePackRun,
  useLabelVersions,
  usePackable,
  usePackLines,
  usePackRuns,
  useSkus,
  type Packable,
  type PackRun,
} from '../../api/pack'
import { useLocationMembers, useSites } from '../../api/store'
import { ActionButton } from '../../components/Can'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  FactRow,
  Facts,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  Tag,
  TwoLine,
  type BoardColumn,
} from '../../components/board'
import { FormDialog } from '../../components/FormDialog'
import { boardDate } from '../../model/payoutBoard'
import { kg2, labState, packableReason, plannedPouches } from '../../model/packing'
import { BoardCard, LabPill } from './shared'

/**
 * Allocate to a pack run (alignment map V1-F7-01a Allocate; F7.1): pick the product and the batch,
 * then Create pack run. V1 defaults: the whole batch, the one packing hall and its line; the
 * operator who starts the run on the phone runs it (uc-core still asks for one, so the hall's
 * machine operator is sent). Listed: brand batches of one farmer from a balanced run, with a
 * sealed QC sample, not on hold, with kilos left; and leftover sacks of earlier pack runs.
 */
export function AllocatePage() {
  const sites = useSites()
  const halls = useMemo(
    () => (sites.data ?? []).filter((s) => s.kind === 'PACKING' && s.active),
    [sites.data],
  )
  const [hallPick, setHallPick] = useState('')
  const hall = halls.find((h) => h.id === hallPick) ?? halls[0] ?? null

  const offered = useSkus(hall ? { approvedAtLocationId: hall.id } : {})
  const drafts = useLabelVersions({ status: 'DRAFT' })
  const approved = useLabelVersions({ status: 'APPROVED' })
  const [skuPick, setSkuPick] = useState('')
  const skus = hall ? (offered.data ?? []) : []
  const sku = skus.find((s) => s.id === skuPick) ?? skus[0] ?? null
  const skuLabel = (approved.data?.rows ?? []).find(
    (l) =>
      l.skuId === sku?.id && (!hall || l.premisesId === hall.id || l.premisesCode === hall.code),
  )
  const waiting = (drafts.data?.rows ?? []).filter((l) => !skus.some((s) => s.id === l.skuId))

  const packable = usePackable(sku?.id ?? null)
  const [batchPick, setBatchPick] = useState<string | null>(null)
  const [searchCode, setSearchCode] = useState('')
  const [found, setFound] = useState<Packable | null>(null)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [searching, setSearching] = useState(false)
  const rows = packable.data ?? []
  const selected =
    rows.find((b) => b.unitId === batchPick) ??
    (found?.eligible && found.unitId === batchPick ? found : null) ??
    null

  const lines = usePackLines(hall?.id ?? null)
  const line = pickPackLine(lines.data ?? [])
  const members = useLocationMembers(hall?.id ?? null)
  const operator = (members.data ?? []).find((m) => m.roles.includes('machine_operator')) ?? null

  const create = useCreatePackRun()
  const pouches = selected && sku ? plannedPouches(selected.availableG, sku.netG) : 0

  const blocked = !hall
    ? 'No packing hall is set up.'
    : !sku
      ? 'No product has an approved label at this hall (F7.4).'
      : !selected
        ? 'Pick a batch.'
        : !line
          ? `No pack line is registered at ${hall.code}.`
          : !operator
            ? `No machine operator is on the ${hall.code} team.`
            : pouches < 1
              ? 'Less than one pouch of kilos left.'
              : null

  const submit = async () => {
    if (blocked || !hall || !sku || !selected || !line || !operator) return
    try {
      await create.mutateAsync({
        skuId: sku.id,
        batchUnitId: selected.unitId,
        plannedKg: gramsToKgString(selected.availableG),
        locationId: hall.id,
        equipmentId: line.id,
        operatorId: operator.userId,
        plannedDate: istDateKey(),
      })
      setBatchPick(null)
      setFound(null)
    } catch {
      /* toasted */
    }
  }

  const search = async () => {
    if (!searchCode.trim()) return
    setSearching(true)
    setSearchError(null)
    try {
      const p = await lookupPackable(searchCode)
      setFound(p)
      if (p?.eligible) setBatchPick(p.unitId)
      if (!p) setSearchError('No batch with that code.')
    } catch (e) {
      setFound(null)
      setSearchError(errorMessage(e))
    } finally {
      setSearching(false)
    }
  }

  const columns: BoardColumn<Packable>[] = [
    {
      id: 'pick',
      header: <span className="sr-only">Pick</span>,
      width: '36px',
      cell: (b) => (
        <input
          type="radio"
          name="allocate-batch"
          aria-label={`Pick ${b.code}`}
          checked={selected?.unitId === b.unitId}
          onChange={() => setBatchPick(b.unitId)}
          className="mt-1 h-5 w-5 accent-ink"
        />
      ),
    },
    {
      id: 'batch',
      header: 'Batch · farmer lot',
      cell: (b) => (
        <TwoLine
          top={b.code}
          sub={b.leftoverOf ? `leftover of ${b.leftoverOf}` : b.parentLotCodes.join(', ') || '—'}
        >
          {b.leftoverOf && <Tag tone="plain">Leftover sack</Tag>}
        </TwoLine>
      ),
    },
    {
      id: 'kg',
      header: 'Available',
      align: 'right',
      cell: (b) => (
        <span className="flex flex-col items-end leading-tight">
          <b>{kg2(b.availableG)}</b>
          <span className="text-[13px] text-muted">
            {b.sacks} sack{b.sacks === 1 ? '' : 's'}
          </span>
        </span>
      ),
    },
    { id: 'at', header: 'Sacks are at', hideBelow: 'sm', cell: (b) => b.places.join(', ') || '—' },
    { id: 'lab', header: 'Lab', cell: (b) => <LabPill overall={b.labStatus} /> },
  ]

  return (
    <BoardPage
      title="Allocate to a pack run"
      actions={
        <LinkButton to="/c/packing/labels" icon={<TagIcon aria-hidden="true" />}>
          Label versions
        </LinkButton>
      }
    >
      <BoardColumns
        main={
          <>
            {halls.length > 1 && (
              <BoardCard title="Packing hall">
                <Select
                  aria-label="Packing hall"
                  value={hall?.id ?? ''}
                  onChange={(v) => {
                    setHallPick(v)
                    setBatchPick(null)
                  }}
                  options={halls.map((h) => ({ value: h.id, label: `${h.code} · ${h.name}` }))}
                />
              </BoardCard>
            )}
            <BoardCard title="1 · Product (SKU)">
              {offered.isLoading || sites.isLoading ? (
                <Note>Loading…</Note>
              ) : skus.length === 0 ? (
                <Pill tone="bad" wrap>
                  {hall
                    ? `No product has an approved label at ${hall.code}.`
                    : 'No packing hall is set up.'}
                </Pill>
              ) : (
                <>
                  <Select
                    aria-label="Product"
                    value={sku?.id ?? ''}
                    onChange={(v) => {
                      setSkuPick(v)
                      setBatchPick(null)
                    }}
                    options={skus.map((s) => ({
                      value: s.id,
                      label: `${s.name} · ${s.netG} g pouch (${s.code})`,
                    }))}
                  />
                  {skuLabel && (
                    <Pill tone="ok">
                      Label {skuLabel.code} approved for {hall?.code}
                    </Pill>
                  )}
                </>
              )}
              {waiting.map((l) => (
                <Note key={l.id}>
                  {l.skuName} ({l.skuCode}) is not offered here: its label {l.code} is waiting for
                  approval (F7.4).
                </Note>
              ))}
            </BoardCard>

            <BoardCard title="2 · Batch">
              <BoardTable<Packable>
                caption="Batches that can be packed"
                columns={columns}
                rows={rows}
                getRowId={(b) => b.unitId}
                selectedId={selected?.unitId ?? null}
                onRowClick={(b) => setBatchPick(b.unitId)}
                loading={packable.isLoading}
                error={packable.error ? errorMessage(packable.error) : undefined}
                empty={sku ? 'No batch of this crop can be packed now.' : 'Pick a product first.'}
              />
              <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  void search()
                }}
              >
                <span className="text-[14px] text-muted">Not in the list?</span>
                <Input
                  aria-label="Search a batch code"
                  placeholder="Search a batch code"
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value)}
                  className="h-9 max-w-[260px]"
                />
                <ActionButton
                  type="submit"
                  size="sm"
                  variant="secondary"
                  loading={searching}
                  leadingIcon={<MagnifyingGlass />}
                >
                  Search
                </ActionButton>
              </form>
              {searchError && <Note className="text-danger">{searchError}</Note>}
              {found && !found.eligible && (
                <div className="flex flex-col gap-1 rounded-card border-[1.5px] border-danger bg-danger-wash/40 px-3 py-2">
                  <b>{found.code}</b>
                  {found.reasons.map((r) => (
                    <span key={r} className="text-[14px] text-danger">
                      {packableReason(r)}
                    </span>
                  ))}
                </div>
              )}
              {found?.eligible && !rows.some((r) => r.unitId === found.unitId) && (
                <Note>
                  {found.code} can be packed but is of another crop than this product, or not
                  listed: {kg2(found.availableG)} · lab{' '}
                  {labState(found.labStatus).text.toLowerCase()}.
                </Note>
              )}
              <Note>
                Listed only: brand batches from one farmer and a balanced run, with a sealed QC
                sample, not on hold, with kilos left; and leftover sacks of earlier pack runs.
              </Note>
            </BoardCard>
            <PlannedRuns />
          </>
        }
        side={
          <SidePanel label="New pack run" title="New pack run">
            <Facts>
              <FactRow label="Product">{sku ? `${sku.code} · ${sku.netG} g pouch` : '—'}</FactRow>
              <FactRow label="Batch">{selected?.code ?? '—'}</FactRow>
              <FactRow label="Kilos" strong>
                {selected ? `${kg2(selected.availableG)} · whole batch` : '—'}
              </FactRow>
              <FactRow label="Planned pouches" strong>
                {selected ? pouches : '—'}
              </FactRow>
              {selected && sku && (
                <FactRow>
                  <Note>
                    {(selected.availableG / 1000).toFixed(2)} × 1000 ÷ {sku.netG} g, rounded down
                  </Note>
                </FactRow>
              )}
            </Facts>
            <Note className="text-ink">
              Packed on {hall?.code ?? '—'}
              {line ? ` · ${line.code}` : ''}. The operator is whoever starts the run on the phone,
              and can never sign this lot’s release.
            </Note>
            {selected && selected.labStatus !== 'PASS' && (
              <Pill tone="wait" wrap>
                Lab {labState(selected.labStatus).text.toLowerCase()}. Allowed now; release waits
                for the result.
              </Pill>
            )}
            <div className="flex-1" />
            <PanelActions>
              <ActionButton
                perm="pack-runs:create"
                variant="primary"
                leadingIcon={<Package />}
                blockedReason={blocked}
                loading={create.isPending}
                onClick={() => void submit()}
              >
                Create pack run
              </ActionButton>
              <ActionButton
                variant="secondary"
                disabled={!selected}
                onClick={() => setBatchPick(null)}
              >
                Cancel
              </ActionButton>
              {blocked && selected && <Note>{blocked}</Note>}
            </PanelActions>
          </SidePanel>
        }
      />
    </BoardPage>
  )
}

/** Runs planned and not started yet: cancel one picked by mistake. */
function PlannedRuns() {
  const runs = usePackRuns({ status: 'OPEN' })
  const planned = (runs.data?.rows ?? []).filter((r) => !r.startedAt)
  const cancel = useCancelPackRun()
  const [target, setTarget] = useState<PackRun | null>(null)
  const [reason, setReason] = useState('')
  if (planned.length === 0) return null
  const columns: BoardColumn<PackRun>[] = [
    {
      id: 'code',
      header: 'Pack run',
      cell: (r) => (
        <TwoLine
          top={r.code}
          sub={r.plannedDate ? `planned ${boardDate(r.plannedDate)}` : undefined}
        />
      ),
    },
    { id: 'batch', header: 'Batch', cell: (r) => r.batchCode ?? '—' },
    { id: 'kg', header: 'Kilos', align: 'right', cell: (r) => kg2(r.plannedG) },
    { id: 'pouches', header: 'Pouches', align: 'right', cell: (r) => r.plannedPouches ?? '—' },
    {
      id: 'where',
      header: 'Hall · line',
      hideBelow: 'sm',
      cell: (r) => `${r.locationCode ?? '—'} · ${r.equipmentCode ?? '—'}`,
    },
    {
      id: 'act',
      header: <span className="sr-only">Cancel</span>,
      cell: (r) => (
        <ActionButton
          perm="pack-runs:create"
          hide
          size="sm"
          variant="danger"
          onClick={() => setTarget(r)}
        >
          Cancel run
        </ActionButton>
      ),
    },
  ]
  return (
    <BoardCard title="Planned, not started yet">
      <BoardTable<PackRun>
        caption="Planned pack runs"
        columns={columns}
        rows={planned}
        getRowId={(r) => r.id}
      />
      <Note>
        The phone shows a planned run to the operator at the hall; once started it is closed, never
        cancelled.
      </Note>
      <FormDialog
        open={Boolean(target)}
        onClose={() => {
          setTarget(null)
          setReason('')
        }}
        title={`Cancel ${target?.code ?? ''}`}
        saveLabel="Cancel run"
        tone="danger"
        loading={cancel.isPending}
        canSave={reason.trim().length >= 3}
        onSave={() => cancel.mutateAsync({ id: target!.id, reason: reason.trim() })}
      >
        <Input
          aria-label="Reason"
          placeholder="Why (e.g. wrong product picked)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </FormDialog>
    </BoardCard>
  )
}
